import { z } from "zod";
export const tabularDataSchemaZod = z.object({
    indexColumn: z
        .object({
        type: z.enum(["timestamp", "number", "string"]),
        name: z.string().min(1),
        required: z.boolean(),
    })
        .optional(),
    columns: z
        .array(z.object({
        name: z.string().min(1),
        type: z.enum(["number", "string", "datetime"]),
        required: z.boolean(),
        unit: z.string().optional(),
        /** A text column's dropdown values; absent is free text. */
        options: z.array(z.string()).optional(),
    }))
        .min(1),
});
function createIndexValidator(indexColumn) {
    switch (indexColumn.type) {
        case "timestamp":
            return indexColumn.required
                ? z.coerce.date()
                : z.coerce.date().optional();
        case "number":
            return indexColumn.required
                ? z.coerce.number()
                : z.coerce.number().optional();
        case "string":
            return indexColumn.required ? z.string().min(1) : z.string().optional();
        default:
            return z.unknown();
    }
}
function createColumnValidator(column) {
    switch (column.type) {
        case "number":
            return column.required
                ? z.coerce.number()
                : z.union([z.coerce.number(), z.null()]);
        case "string":
            return column.required
                ? z.string().min(1)
                : z.union([z.string(), z.null()]);
        case "datetime":
            return column.required
                ? z.coerce.date()
                : z.union([z.coerce.date(), z.null()]);
        default:
            return z.unknown();
    }
}
export function createTabularDataValidator(schema) {
    return z.object({
        rows: z.array(z.object({
            index: schema.indexColumn
                ? createIndexValidator(schema.indexColumn)
                : z.unknown().optional(),
            data: z.object(Object.fromEntries(schema.columns.map((col) => [col.name, createColumnValidator(col)]))),
        })),
    });
}
// Helper function to validate tabular data against a schema
export function validateTabularData(data, schema) {
    const validator = createTabularDataValidator(schema);
    return validator.safeParse(data);
}
// Helper function to create an empty tabular data structure
export function createEmptyTabularData() {
    return {
        rows: [],
        metadata: {
            columnWidths: {},
        },
    };
}
// Helper function to add a new row to tabular data
export function addRowToTabularData(data, schema, initialValues) {
    const newRow = {
        index: initialValues?.index ??
            (schema.indexColumn?.type === "timestamp" ? new Date() : undefined),
        data: Object.fromEntries(schema.columns.map((col) => [
            col.name,
            initialValues?.data?.[col.name] ?? null,
        ])),
    };
    return {
        rows: [...data.rows, newRow],
        metadata: data.metadata,
    };
}
// Helper function to update column width in metadata
export function updateColumnWidth(data, columnKey, width) {
    return {
        ...data,
        metadata: {
            ...data.metadata,
            columnWidths: {
                ...data.metadata?.columnWidths,
                [columnKey]: width,
            },
        },
    };
}
// Helper function to get column width from metadata
export function getColumnWidth(data, columnKey, defaultWidth = 120) {
    return data.metadata?.columnWidths?.[columnKey] ?? defaultWidth;
}
// Helper function to get default column widths for a schema
export function getDefaultColumnWidths(schema) {
    const widths = {};
    if (schema.indexColumn) {
        widths[schema.indexColumn.name] = 100; // Smaller default for index columns
    }
    schema.columns.forEach((col) => {
        widths[col.name] = 120; // Default column width
    });
    return widths;
}
//# sourceMappingURL=tabular-schema.js.map